import React, { useState } from 'react';
import ArrowBack from '@mui/icons-material/ArrowBack';
import EditOutlined from '@mui/icons-material/EditOutlined';
import ChevronRight from '@mui/icons-material/ChevronRight';
import SpeakerOutlined from '@mui/icons-material/SpeakerOutlined';
import MemoryOutlined from '@mui/icons-material/MemoryOutlined';
import GraphicEqOutlined from '@mui/icons-material/GraphicEqOutlined';
import MicOutlined from '@mui/icons-material/MicOutlined';
import SensorsOutlined from '@mui/icons-material/SensorsOutlined';
import SettingsInputComponentOutlined from '@mui/icons-material/SettingsInputComponentOutlined';
import DevicesOtherOutlined from '@mui/icons-material/DevicesOtherOutlined';
import { Card, Badge, Button, IconButton, EmptyState, PageLoader, Alert } from '../../../components/ui';
import PasControllerDialog from './PasControllerDialog';

/**
 * Device types used to be told apart by eight two-stop gradients (blue→indigo,
 * emerald→green, amber→orange…). An icon identifies a type far faster than a
 * colour the reader has to learn, and it survives being printed or viewed by
 * someone who can't distinguish the hues.
 */
const TYPE_ICONS = {
    Mainframe: MemoryOutlined,
    Speaker: SpeakerOutlined,
    Loop: GraphicEqOutlined,
    'Line Array': SpeakerOutlined,
    'Noise Sensor': SensorsOutlined,
    'Digital Audio Bridge': SettingsInputComponentOutlined,
    'Mic Station': MicOutlined,
    IED: MemoryOutlined,
};

const DeviceTypeSelector = ({
    controller,
    deviceTypes = [],
    deviceCounts = {},
    onSelectType,
    onControllerChanged,
    onControllerDeleted,
    onBack,
    loading,
    errorMessage,
}) => {
    const [editingController, setEditingController] = useState(false);
    const controllerName = controller?.description || controller?.name || 'Controller';
    const controllerIp = controller?.ipAddress || controller?.ip || 'N/A';

    return (
        <div className="animate-fade-in">
            <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-center gap-3">
                    <IconButton icon={ArrowBack} label="Back to controllers" onClick={onBack} variant="secondary" />
                    <div className="min-w-0">
                        <p className="text-sm text-muted-fg">Devices in {controllerName}</p>
                        <h2 className="text-lg font-semibold text-foreground sm:text-xl">
                            Choose a device type
                        </h2>
                    </div>
                </div>

                <div className="flex shrink-0 flex-col gap-2">
                    <dl className="rounded-lg border border-border bg-surface px-4 py-3 text-sm shadow-sm">
                        <dt className="text-xs uppercase tracking-wide text-muted-fg">Controller</dt>
                        <dd className="mt-0.5 font-medium text-foreground">{controllerName}</dd>
                        <dd className="tabular mt-1 text-xs text-muted-fg">{controllerIp}</dd>
                        {controller?.macAddress && <dd className="tabular mt-1 text-xs text-muted-fg">{controller.macAddress}</dd>}
                        {controller?.location?.code && (
                            <dd className="mt-1 text-xs uppercase tracking-wide text-muted-fg">
                                {controller.location.code}
                            </dd>
                        )}
                    </dl>
                    <Button variant="secondary" icon={EditOutlined} onClick={() => setEditingController(true)}>
                        Edit controller
                    </Button>
                </div>
            </div>

            {loading && <PageLoader label="Loading devices…" />}

            {!loading && errorMessage && <Alert variant="error">{errorMessage}</Alert>}

            {!loading && !errorMessage && deviceTypes.length === 0 && (
                <EmptyState
                    icon={DevicesOtherOutlined}
                    title="No devices found"
                    description="This controller has no devices attached."
                />
            )}

            {!loading && !errorMessage && deviceTypes.length > 0 && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {deviceTypes.map((type) => {
                        const Icon = TYPE_ICONS[type] || DevicesOtherOutlined;
                        const count = deviceCounts[type] || 0;

                        return (
                            <Card
                                key={type}
                                interactive
                                onClick={() => onSelectType && onSelectType(type)}
                                className="group"
                            >
                                <div className="flex items-center gap-4 p-5">
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-soft text-accent transition-colors duration-base group-hover:bg-accent group-hover:text-accent-fg">
                                        <Icon style={{ fontSize: 22 }} aria-hidden="true" />
                                    </span>

                                    <div className="min-w-0 flex-1">
                                        <h3 className="truncate font-semibold text-foreground">{type}</h3>
                                        <p className="mt-0.5 text-sm text-muted-fg">
                                            {count} {count === 1 ? 'device' : 'devices'}
                                        </p>
                                    </div>

                                    <Badge variant="neutral" size="sm">{count}</Badge>
                                    <ChevronRight
                                        style={{ fontSize: 20 }}
                                        aria-hidden="true"
                                        className="shrink-0 text-muted-fg transition-transform duration-base group-hover:translate-x-1 group-hover:text-accent"
                                    />
                                </div>
                            </Card>
                        );
                    })}
                </div>
            )}

            <PasControllerDialog
                isOpen={editingController}
                controller={controller}
                onClose={() => setEditingController(false)}
                onChanged={onControllerChanged}
                onDeleted={onControllerDeleted}
            />
        </div>
    );
};

export default DeviceTypeSelector;
